import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Feature, Component, TestCase, Platform, PlatformItem } from '../../../types';
import { apiRequest } from '../../../utils/api';
import { useToast, showTempMessage } from '../../../utils/messages';
import { Toast } from '../../../utils/messages';
import { useProduct } from '../../../contexts/ProductContext';
import FeatureTreeSidebar from './FeatureTreeSidebar';
import ComponentDetailPanel from './ComponentDetailPanel';
import GlobalActionBar from './GlobalActionBar';
import FeatureImporter from '../FeatureImporter';
import CreateFeatureModal from '../CreateFeatureModal';
import TestCaseReviewModal from '../TestCaseReviewModal';
import ConfirmDialog from '../../shared/ConfirmDialog';
import MilestoneSelector from '../../shared/MilestoneSelector';
import '../../../App.css';

const Library: React.FC = () => {
  const navigate = useNavigate();
  const { selectedProduct, products } = useProduct();
  const selectedProductId = selectedProduct?.id || '';
  
  // Data state
  const [features, setFeatures] = useState<Feature[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [availablePlatforms, setAvailablePlatforms] = useState<PlatformItem[]>([]);

  // Selection state
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<string>>(new Set());
  const [selectedComponentIds, setSelectedComponentIds] = useState<Set<string>>(new Set());
  const [selectedTestCaseIds, setSelectedTestCaseIds] = useState<Set<string>>(new Set());
  const [activeComponentId, setActiveComponentId] = useState<string | null>(null);

  // UI state
  const [searchText, setSearchText] = useState('');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreateFeatureModal, setShowCreateFeatureModal] = useState(false);
  const [showCreateComponentModal, setShowCreateComponentModal] = useState(false);
  const [viewingTestCase, setViewingTestCase] = useState<TestCase | null>(null);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);

  // Component modal state
  const [componentModalFeatureId, setComponentModalFeatureId] = useState<string | null>(null);
  const [componentModalName, setComponentModalName] = useState('');
  const [componentModalDescription, setComponentModalDescription] = useState('');
  const [componentModalPlatforms, setComponentModalPlatforms] = useState<Platform[]>([]);
  const [componentModalMilestone, setComponentModalMilestone] = useState<string>('');

  // Confirmation dialogs
  const [confirmDelete, setConfirmDelete] = useState<{
    type: 'feature' | 'component' | 'testcase' | 'bulk';
    ids: string[];
    name: string;
  } | null>(null);

  // Move modal
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTargetComponentId, setMoveTargetComponentId] = useState<string>('');
  const [moveTargetFeatureId, setMoveTargetFeatureId] = useState<string>('');
  const [moveType, setMoveType] = useState<'testcases' | 'components'>('testcases');

  // Bulk edit modal
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditType, setBulkEditType] = useState<'testcases' | 'components'>('testcases');
  // Test case bulk edit fields
  const [bulkAutomationStatus, setBulkAutomationStatus] = useState<'auto' | 'manual' | 'candidate' | ''>('');
  const [bulkPreConditions, setBulkPreConditions] = useState<string>('');
  // Component bulk edit fields
  const [bulkMilestone, setBulkMilestone] = useState<string>('');
  const [bulkPlatforms, setBulkPlatforms] = useState<string[]>([]);

  // Create dropdown
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const createDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target as Node)) {
        setShowCreateDropdown(false);
      }
    };
    if (showCreateDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCreateDropdown]);

  const { message, setMessage, showMessage } = useToast();

  // Load platforms
  useEffect(() => {
    const loadPlatforms = async () => {
      try {
        const platformsData = await apiRequest<PlatformItem[]>('/api/platforms', {}, { useAuth: true });
        const allPlatforms = Array.isArray(platformsData) ? platformsData : [];
        const filteredPlatforms = selectedProductId
          ? allPlatforms.filter(p => 
              !p.productIds || 
              p.productIds.length === 0 || 
              p.productIds.includes(selectedProductId)
            )
          : allPlatforms;
        setAvailablePlatforms(filteredPlatforms);
      } catch (error) {
        console.error('Error fetching platforms:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPlatforms();
  }, [selectedProduct, selectedProductId]);

  // Fetch features
  useEffect(() => {
    if (!selectedProductId) {
      setFeatures([]);
      setComponents([]);
      setTestCases([]);
      return;
    }
    
    const fetchFeatures = async () => {
      try {
        const data = await apiRequest<Feature[]>(`/api/features?productId=${selectedProductId}`, {}, { useAuth: true });
        setFeatures(data);
      } catch (error) {
        console.error('Error fetching features:', error);
      }
    };
    fetchFeatures();
  }, [selectedProductId]);

  // Fetch components
  useEffect(() => {
    if (features.length === 0) {
      setComponents([]);
      setTestCases([]);
      return;
    }
    
    const fetchComponents = async () => {
      try {
        const componentsData = await apiRequest<Component[]>('/api/components', {}, { useAuth: true }).catch(() => []);
        if (Array.isArray(componentsData)) {
          const featureIds = features.map(f => f.id);
          const filtered = componentsData.filter((c: Component) => featureIds.includes(c.featureId));
          setComponents(filtered);
          
          // Auto-select first component if none selected
          setActiveComponentId(prev => prev || filtered[0]?.id || null);
        }
      } catch (error) {
        console.error('Error fetching components:', error);
        setComponents([]);
      }
    };
    fetchComponents();
  }, [features]);

  // Fetch test cases
  useEffect(() => {
    if (features.length === 0) {
      setTestCases([]);
      return;
    }
    
    const fetchTestCases = async () => {
      try {
        const testCasesData = await apiRequest<TestCase[]>('/api/testcases', {}, { useAuth: true }).catch(() => []);
        if (Array.isArray(testCasesData)) {
          const componentIds = components.map(c => c.id);
          const featureIds = features.map(f => f.id);
          const filtered = testCasesData.filter((tc: TestCase) => 
            (tc.componentId && componentIds.includes(tc.componentId)) ||
            (!tc.componentId && tc.featureId && featureIds.includes(tc.featureId))
          );
          setTestCases(filtered);
        }
      } catch (error) {
        console.error('Error fetching test cases:', error);
        setTestCases([]);
      }
    };
    fetchTestCases();
  }, [components, features]);

  // Selection handlers
  const handleFeatureSelect = (featureId: string, selected: boolean) => {
    setSelectedFeatureIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(featureId);
        // Also select all components under this feature
        const featureComponents = components.filter(c => c.featureId === featureId);
        featureComponents.forEach(c => selectedComponentIds.add(c.id));
        setSelectedComponentIds(new Set(selectedComponentIds));
      } else {
        next.delete(featureId);
        // Also deselect all components under this feature
        const featureComponents = components.filter(c => c.featureId === featureId);
        featureComponents.forEach(c => selectedComponentIds.delete(c.id));
        setSelectedComponentIds(new Set(selectedComponentIds));
      }
      return next;
    });
  };

  const handleComponentSelect = (componentId: string, selected: boolean) => {
    setSelectedComponentIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(componentId);
      } else {
        next.delete(componentId);
      }
      return next;
    });
  };

  const handleTestCaseSelect = (testCaseId: string, selected: boolean) => {
    setSelectedTestCaseIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(testCaseId);
      } else {
        next.delete(testCaseId);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedFeatureIds(new Set());
    setSelectedComponentIds(new Set());
    setSelectedTestCaseIds(new Set());
  };

  // Action handlers
  const handleMove = () => {
    // Determine what type of move based on selection
    if (selectedComponentIds.size > 0 && selectedTestCaseIds.size === 0) {
      setMoveType('components');
      setMoveTargetFeatureId('');
    } else {
      setMoveType('testcases');
      setMoveTargetComponentId('');
    }
    setShowMoveModal(true);
  };

  const handleDelete = () => {
    const totalSelected = selectedFeatureIds.size + selectedComponentIds.size + selectedTestCaseIds.size;
    if (totalSelected === 0) return;

    setConfirmDelete({
      type: 'bulk',
      ids: [
        ...Array.from(selectedFeatureIds),
        ...Array.from(selectedComponentIds),
        ...Array.from(selectedTestCaseIds)
      ],
      name: `${totalSelected} item${totalSelected !== 1 ? 's' : ''}`
    });
  };

  const handleBulkEdit = () => {
    // Determine what type of bulk edit based on selection
    if (selectedComponentIds.size > 0 && selectedTestCaseIds.size === 0) {
      setBulkEditType('components');
      setBulkMilestone('');
      setBulkPlatforms([]);
    } else {
      setBulkEditType('testcases');
      setBulkAutomationStatus('');
      setBulkPreConditions('');
    }
    setShowBulkEditModal(true);
  };

  const handleBulkEditConfirm = async () => {
    if (bulkEditType === 'components') {
      // Bulk edit components
      const updates: Partial<Component> = {};
      if (bulkMilestone) updates.milestone = bulkMilestone;
      if (bulkPlatforms.length > 0) updates.platforms = bulkPlatforms as Platform[];

      if (Object.keys(updates).length === 0) {
        showTempMessage(setMessage, { type: 'error', text: 'No changes to apply' });
        return;
      }

      try {
        const componentIdsArray = Array.from(selectedComponentIds);
        for (const componentId of componentIdsArray) {
          await apiRequest<Component>(`/api/components/${componentId}`, {
            method: 'PUT',
            body: JSON.stringify(updates)
          }, { useAuth: true });
        }

        // Update local state
        setComponents(prev => prev.map(c => {
          if (selectedComponentIds.has(c.id)) {
            return { ...c, ...updates };
          }
          return c;
        }));

        showTempMessage(setMessage, { 
          type: 'success', 
          text: `Updated ${selectedComponentIds.size} component${selectedComponentIds.size !== 1 ? 's' : ''}` 
        });
        clearSelection();
        setShowBulkEditModal(false);
      } catch (error) {
        console.error('Error bulk editing components:', error);
        showTempMessage(setMessage, { type: 'error', text: 'Failed to update components' });
      }
    } else {
      // Bulk edit test cases
      const updates: Partial<TestCase> = {};
      if (bulkAutomationStatus) {
        updates.automated = bulkAutomationStatus === 'auto';
        updates.automationCandidate = bulkAutomationStatus === 'candidate';
      }
      if (bulkPreConditions) updates.preConditions = bulkPreConditions;

      if (Object.keys(updates).length === 0) {
        showTempMessage(setMessage, { type: 'error', text: 'No changes to apply' });
        return;
      }

      try {
        const testCaseIdsArray = Array.from(selectedTestCaseIds);
        for (const testCaseId of testCaseIdsArray) {
          await apiRequest<TestCase>(`/api/testcases/${testCaseId}`, {
            method: 'PUT',
            body: JSON.stringify(updates)
          }, { useAuth: true });
        }

        // Update local state
        setTestCases(prev => prev.map(tc => {
          if (selectedTestCaseIds.has(tc.id)) {
            return { ...tc, ...updates };
          }
          return tc;
        }));

        showTempMessage(setMessage, { 
          type: 'success', 
          text: `Updated ${selectedTestCaseIds.size} test case${selectedTestCaseIds.size !== 1 ? 's' : ''}` 
        });
        clearSelection();
        setShowBulkEditModal(false);
      } catch (error) {
        console.error('Error bulk editing test cases:', error);
        showTempMessage(setMessage, { type: 'error', text: 'Failed to update test cases' });
      }
    }
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;

    try {
      // Delete test cases
      const testCaseIdsArray = Array.from(selectedTestCaseIds);
      for (const id of testCaseIdsArray) {
        await apiRequest(`/api/testcases/${id}`, { method: 'DELETE' }, { useAuth: true });
      }
      setTestCases(prev => prev.filter(tc => !selectedTestCaseIds.has(tc.id)));

      // Delete components
      const componentIdsArray = Array.from(selectedComponentIds);
      for (const id of componentIdsArray) {
        await apiRequest(`/api/components/${id}`, { method: 'DELETE' }, { useAuth: true });
      }
      setComponents(prev => prev.filter(c => !selectedComponentIds.has(c.id)));

      // Delete features
      const featureIdsArray = Array.from(selectedFeatureIds);
      for (const id of featureIdsArray) {
        await apiRequest(`/api/features/${id}`, { method: 'DELETE' }, { useAuth: true });
      }
      setFeatures(prev => prev.filter(f => !selectedFeatureIds.has(f.id)));

      showTempMessage(setMessage, { type: 'success', text: `Deleted ${confirmDelete.name}` });
      clearSelection();
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error deleting items:', error);
      showTempMessage(setMessage, { type: 'error', text: 'Failed to delete some items' });
      setConfirmDelete(null);
    }
  };

  const handleMoveConfirm = async () => {
    if (moveType === 'components') {
      // Move components to a different feature
      if (!moveTargetFeatureId) return;

      try {
        const componentIdsArray = Array.from(selectedComponentIds);
        for (const componentId of componentIdsArray) {
          await apiRequest<Component>(`/api/components/${componentId}`, {
            method: 'PUT',
            body: JSON.stringify({ featureId: moveTargetFeatureId })
          }, { useAuth: true });
        }

        // Update local state
        setComponents(prev => prev.map(c => {
          if (selectedComponentIds.has(c.id)) {
            return { ...c, featureId: moveTargetFeatureId };
          }
          return c;
        }));

        const targetFeature = features.find(f => f.id === moveTargetFeatureId);
        showTempMessage(setMessage, { 
          type: 'success', 
          text: `Moved ${selectedComponentIds.size} component${selectedComponentIds.size !== 1 ? 's' : ''} to ${targetFeature?.name}` 
        });
        clearSelection();
        setShowMoveModal(false);
        setMoveTargetFeatureId('');
      } catch (error) {
        console.error('Error moving components:', error);
        showTempMessage(setMessage, { type: 'error', text: 'Failed to move components' });
      }
    } else {
      // Move test cases to a different component
      if (!moveTargetComponentId) return;

      try {
        const testCaseIdsArray = Array.from(selectedTestCaseIds);
        for (const testCaseId of testCaseIdsArray) {
          await apiRequest<TestCase>(`/api/testcases/${testCaseId}`, {
            method: 'PUT',
            body: JSON.stringify({ componentId: moveTargetComponentId })
          }, { useAuth: true });
        }

        // Update local state
        setTestCases(prev => prev.map(tc => {
          if (selectedTestCaseIds.has(tc.id)) {
            return { ...tc, componentId: moveTargetComponentId };
          }
          return tc;
        }));

        const targetComponent = components.find(c => c.id === moveTargetComponentId);
        showTempMessage(setMessage, { 
          type: 'success', 
          text: `Moved ${selectedTestCaseIds.size} test case${selectedTestCaseIds.size !== 1 ? 's' : ''} to ${targetComponent?.name}` 
        });
        clearSelection();
        setShowMoveModal(false);
        setMoveTargetComponentId('');
      } catch (error) {
        console.error('Error moving test cases:', error);
        showTempMessage(setMessage, { type: 'error', text: 'Failed to move test cases' });
      }
    }
  };

  // Drag & Drop: Move component to different feature
  const handleComponentDropOnFeature = async (componentId: string, targetFeatureId: string) => {
    const component = components.find(c => c.id === componentId);
    if (!component || component.featureId === targetFeatureId) return;

    try {
      await apiRequest<Component>(`/api/components/${componentId}`, {
        method: 'PUT',
        body: JSON.stringify({ featureId: targetFeatureId })
      }, { useAuth: true });

      // Update local state
      setComponents(prev => prev.map(c => 
        c.id === componentId ? { ...c, featureId: targetFeatureId } : c
      ));

      const targetFeature = features.find(f => f.id === targetFeatureId);
      showTempMessage(setMessage, { 
        type: 'success', 
        text: `Moved "${component.name}" to ${targetFeature?.name}` 
      });
    } catch (error) {
      console.error('Error moving component:', error);
      showTempMessage(setMessage, { type: 'error', text: 'Failed to move component' });
    }
  };

  // Drag & Drop: Move test case to different component
  const handleTestCaseDropOnComponent = async (testCaseId: string, targetComponentId: string) => {
    const testCase = testCases.find(tc => tc.id === testCaseId);
    if (!testCase || testCase.componentId === targetComponentId) return;

    try {
      await apiRequest<TestCase>(`/api/testcases/${testCaseId}`, {
        method: 'PUT',
        body: JSON.stringify({ componentId: targetComponentId })
      }, { useAuth: true });

      // Update local state
      setTestCases(prev => prev.map(tc => 
        tc.id === testCaseId ? { ...tc, componentId: targetComponentId } : tc
      ));

      const targetComponent = components.find(c => c.id === targetComponentId);
      showTempMessage(setMessage, { 
        type: 'success', 
        text: `Moved test case to ${targetComponent?.name}` 
      });
    } catch (error) {
      console.error('Error moving test case:', error);
      showTempMessage(setMessage, { type: 'error', text: 'Failed to move test case' });
    }
  };

  // Create handlers
  const handleCreateFeature = () => {
    setEditingFeature(null);
    setShowCreateFeatureModal(true);
    setShowCreateDropdown(false);
  };

  const handleCreateComponent = (featureId?: string) => {
    setComponentModalFeatureId(featureId || null);
    setComponentModalName('');
    setComponentModalDescription('');
    setComponentModalPlatforms([]);
    setComponentModalMilestone('');
    setShowCreateComponentModal(true);
    setShowCreateDropdown(false);
  };

  const handleCreateTestCase = () => {
    setShowCreateDropdown(false);
    // Navigate to test case creation page, passing componentId if one is active
    navigate('/library/testcase/new', { 
      state: activeComponentId ? { componentId: activeComponentId } : undefined 
    });
  };

  const handleSubmitComponent = async () => {
    if (!componentModalFeatureId) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select a feature' });
      return;
    }
    if (!componentModalName.trim()) {
      showTempMessage(setMessage, { type: 'error', text: 'Component name is required' });
      return;
    }
    if (!componentModalMilestone.trim()) {
      showTempMessage(setMessage, { type: 'error', text: 'Milestone is required' });
      return;
    }

    try {
      const componentData = {
        name: componentModalName.trim(),
        featureId: componentModalFeatureId,
        description: componentModalDescription.trim() || undefined,
        platforms: componentModalPlatforms.length > 0 ? componentModalPlatforms : undefined,
        milestone: componentModalMilestone.trim()
      };

      const newComponent = await apiRequest<Component>('/api/components', {
        method: 'POST',
        body: JSON.stringify(componentData)
      }, { useAuth: true });

      setComponents(prev => [...prev, newComponent]);
      setActiveComponentId(newComponent.id);
      showTempMessage(setMessage, { type: 'success', text: 'Component created' });
      setShowCreateComponentModal(false);
    } catch (error) {
      console.error('Error creating component:', error);
      showTempMessage(setMessage, { type: 'error', text: 'Failed to create component' });
    }
  };

  const handleFeatureCreated = (feature: Feature) => {
    setFeatures(prev => [...prev, feature]);
  };

  const handleImportComplete = async () => {
    const updatedFeatures = await apiRequest<Feature[]>(`/api/features?productId=${selectedProductId}`, {}, { useAuth: true });
    setFeatures(updatedFeatures || []);
  };

  // Get active component
  const activeComponent = activeComponentId 
    ? components.find(c => c.id === activeComponentId) || null 
    : null;

  if (loading) {
    return <div className="form-container">Loading...</div>;
  }

  const selectedProductName = selectedProduct?.name || '';

  return (
    <div className="library-container">
      <Toast message={message} />

      {/* Global Action Bar */}
      <GlobalActionBar
        selection={{
          features: selectedFeatureIds.size,
          components: selectedComponentIds.size,
          testCases: selectedTestCaseIds.size
        }}
        onMove={handleMove}
        onDelete={handleDelete}
        onBulkEdit={handleBulkEdit}
        onClearSelection={clearSelection}
      />

      {selectedProductId ? (
        <>
          {/* Header */}
          <div className="library-header">
            <div className="library-header-content">
              <h1 className="library-title">{selectedProductName} Library</h1>
              <p className="library-subtitle">
                {features.length} feature{features.length !== 1 ? 's' : ''} • 
                {components.length} component{components.length !== 1 ? 's' : ''} • 
                {testCases.length} test case{testCases.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="library-header-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowImportModal(true)}
              >
                Import
              </button>
              <div className="create-dropdown" ref={createDropdownRef}>
                <button 
                  className="btn btn-primary" 
                  onClick={() => setShowCreateDropdown(!showCreateDropdown)}
                >
                  + Create
                </button>
                {showCreateDropdown && (
                  <div className="create-dropdown-menu">
                    <button 
                      className="create-dropdown-item"
                      onClick={handleCreateFeature}
                    >
                      New Feature
                    </button>
                    <button 
                      className="create-dropdown-item"
                      onClick={() => handleCreateComponent()}
                    >
                      New Component
                    </button>
                    <button 
                      className="create-dropdown-item"
                      onClick={handleCreateTestCase}
                    >
                      New Test Case
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="library-content">
            {/* Left Sidebar - Feature Tree */}
            <FeatureTreeSidebar
              features={features}
              components={components}
              testCases={testCases}
              selectedFeatureIds={selectedFeatureIds}
              selectedComponentIds={selectedComponentIds}
              activeComponentId={activeComponentId}
              onFeatureSelect={handleFeatureSelect}
              onComponentSelect={handleComponentSelect}
              onComponentClick={setActiveComponentId}
              searchText={searchText}
              onSearchChange={setSearchText}
              onComponentDropOnFeature={handleComponentDropOnFeature}
              onTestCaseDropOnComponent={handleTestCaseDropOnComponent}
            />

            {/* Right Panel - Component Details */}
            <ComponentDetailPanel
              component={activeComponent}
              testCases={testCases}
              projectCode={selectedProduct?.projectId}
              selectedTestCaseIds={selectedTestCaseIds}
              onTestCaseSelect={handleTestCaseSelect}
              onTestCaseClick={setViewingTestCase}
            />
          </div>
        </>
      ) : (
        <div className="library-empty-state">
          <div className="library-empty-icon">📦</div>
          <h3>Select a Product</h3>
          <p>Choose a product from the dropdown above to view its library.</p>
        </div>
      )}

      {/* Modals */}
      <FeatureImporter
        selectedProduct={selectedProductId}
        products={products}
        features={features}
        onImportComplete={handleImportComplete}
        setMessage={setMessage}
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
      />

      {showCreateFeatureModal && (
        <CreateFeatureModal
          isOpen={showCreateFeatureModal}
          onClose={() => {
            setShowCreateFeatureModal(false);
            setEditingFeature(null);
          }}
          productId={selectedProductId}
          products={products}
          onFeatureCreated={handleFeatureCreated}
          setMessage={setMessage}
          showMessage={showMessage}
          editingFeature={editingFeature}
          onFeatureUpdated={(updated) => {
            setFeatures(prev => prev.map(f => f.id === updated.id ? updated : f));
            setEditingFeature(null);
            setShowCreateFeatureModal(false);
          }}
        />
      )}

      {showCreateComponentModal && (
        <div className="modal-overlay">
          <div className="card modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="title-lg mb-md">Create Component</h2>
            
            <div className="mb-md">
              <label className="label">Feature <span className="text-danger">*</span></label>
              <select
                className="input"
                value={componentModalFeatureId || ''}
                onChange={(e) => setComponentModalFeatureId(e.target.value || null)}
              >
                <option value="">Select a feature...</option>
                {features.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>

            <div className="mb-md">
              <label className="label">Component Name <span className="text-danger">*</span></label>
              <input
                type="text"
                placeholder="e.g., Auto Route"
                value={componentModalName}
                onChange={(e) => setComponentModalName(e.target.value)}
                className="input"
              />
            </div>

            <div className="mb-md">
              <label className="label">Description</label>
              <textarea
                value={componentModalDescription}
                onChange={(e) => setComponentModalDescription(e.target.value)}
                className="input"
                rows={3}
                placeholder="Component description..."
              />
            </div>

            <div className="mb-md">
              <label className="label">Milestone <span className="text-danger">*</span></label>
              <MilestoneSelector
                value={componentModalMilestone}
                onChange={setComponentModalMilestone}
                productId={selectedProductId}
                placeholder="Select or create milestone"
                required
              />
            </div>

            <div className="mb-md">
              <label className="label">Platforms</label>
              {availablePlatforms.length === 0 ? (
                <p className="hint">No platforms available.</p>
              ) : (
                <div className="platform-controls-group-runner">
                  {availablePlatforms.map(platformItem => {
                    const platform = platformItem.name as Platform;
                    const isSelected = componentModalPlatforms.includes(platform);
                    return (
                      <button
                        key={platformItem.id}
                        type="button"
                        className={`platform-tested-button ${isSelected ? 'tested' : ''}`}
                        onClick={() => {
                          if (isSelected) {
                            setComponentModalPlatforms(componentModalPlatforms.filter(p => p !== platform));
                          } else {
                            setComponentModalPlatforms([...componentModalPlatforms, platform]);
                          }
                        }}
                      >
                        <span className="platform-name">{platform}</span>
                        {isSelected && <span className="platform-icon">✓</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowCreateComponentModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmitComponent}
                disabled={!componentModalFeatureId || !componentModalName.trim() || !componentModalMilestone.trim()}
              >
                Create Component
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingTestCase && (
        <TestCaseReviewModal
          testCase={viewingTestCase}
          feature={features.find(f => {
            const comp = components.find(c => c.id === viewingTestCase.componentId);
            return comp ? f.id === comp.featureId : f.id === viewingTestCase.featureId;
          })}
          component={components.find(c => c.id === viewingTestCase.componentId)}
          projectCode={selectedProduct?.projectId}
          onClose={() => setViewingTestCase(null)}
          onEdit={(tc) => {
            setViewingTestCase(null);
            // Use human-readable ID format: {projectId}-tc-{number}
            const tcId = tc.testCaseNumber && selectedProduct?.projectId 
              ? `${selectedProduct.projectId.toLowerCase()}-tc-${tc.testCaseNumber}`
              : tc.id;
            navigate(`/library/testcase/${tcId}/edit`);
          }}
          onDelete={(id) => {
            setTestCases(prev => prev.filter(tc => tc.id !== id));
            setViewingTestCase(null);
          }}
          zIndex={2000}
        />
      )}

      {/* Move Modal */}
      {showMoveModal && (
        <div className="modal-overlay">
          <div className="card modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="title-lg mb-md">
              {moveType === 'components' ? 'Move Components' : 'Move Test Cases'}
            </h2>
            <p className="mb-md">
              {moveType === 'components' 
                ? `Moving ${selectedComponentIds.size} component${selectedComponentIds.size !== 1 ? 's' : ''} to:`
                : `Moving ${selectedTestCaseIds.size} test case${selectedTestCaseIds.size !== 1 ? 's' : ''} to:`
              }
            </p>
            
            <div className="mb-md">
              <label className="label">
                {moveType === 'components' ? 'Target Feature' : 'Target Component'}
              </label>
              {moveType === 'components' ? (
                <select
                  className="input"
                  value={moveTargetFeatureId}
                  onChange={(e) => setMoveTargetFeatureId(e.target.value)}
                >
                  <option value="">Select a feature...</option>
                  {features.map(feature => (
                    <option key={feature.id} value={feature.id}>{feature.name}</option>
                  ))}
                </select>
              ) : (
                <select
                  className="input"
                  value={moveTargetComponentId}
                  onChange={(e) => setMoveTargetComponentId(e.target.value)}
                >
                  <option value="">Select a component...</option>
                  {features.map(feature => (
                    <optgroup key={feature.id} label={feature.name}>
                      {components.filter(c => c.featureId === feature.id).map(comp => (
                        <option key={comp.id} value={comp.id}>{comp.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}
            </div>

            <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowMoveModal(false);
                  setMoveTargetComponentId('');
                  setMoveTargetFeatureId('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleMoveConfirm}
                disabled={moveType === 'components' ? !moveTargetFeatureId : !moveTargetComponentId}
              >
                Move
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className="modal-overlay">
          <div className="card modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="title-lg mb-md">
              {bulkEditType === 'components' ? 'Bulk Edit Components' : 'Bulk Edit Test Cases'}
            </h2>
            <p className="mb-md text-muted">
              {bulkEditType === 'components' 
                ? `Editing ${selectedComponentIds.size} component${selectedComponentIds.size !== 1 ? 's' : ''}`
                : `Editing ${selectedTestCaseIds.size} test case${selectedTestCaseIds.size !== 1 ? 's' : ''}`
              }
            </p>
            <p className="mb-md text-muted" style={{ fontSize: '0.85rem' }}>
              Only fill in fields you want to change. Empty fields will not be modified.
            </p>
            
            {bulkEditType === 'components' ? (
              <>
                <div className="mb-md">
                  <label className="label">Milestone</label>
                  <MilestoneSelector
                    productId={selectedProductId}
                    value={bulkMilestone}
                    onChange={setBulkMilestone}
                  />
                </div>
                
                <div className="mb-md">
                  <label className="label">Platforms</label>
                  <div className="platform-grid">
                    {availablePlatforms.map(platform => (
                      <label key={platform.id} className="platform-checkbox">
                        <input
                          type="checkbox"
                          checked={bulkPlatforms.includes(platform.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBulkPlatforms(prev => [...prev, platform.name]);
                            } else {
                              setBulkPlatforms(prev => prev.filter(p => p !== platform.name));
                            }
                          }}
                        />
                        {platform.name}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="mb-md">
                  <label className="label">Automation Status</label>
                  <select
                    className="input"
                    value={bulkAutomationStatus}
                    onChange={(e) => setBulkAutomationStatus(e.target.value as 'auto' | 'manual' | 'candidate' | '')}
                  >
                    <option value="">No change</option>
                    <option value="auto">Automated</option>
                    <option value="candidate">To Be Automated</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
                
                <div className="mb-md">
                  <label className="label">Pre-Conditions</label>
                  <textarea
                    className="input"
                    rows={3}
                    placeholder="Leave empty to keep existing values..."
                    value={bulkPreConditions}
                    onChange={(e) => setBulkPreConditions(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowBulkEditModal(false);
                  setBulkAutomationStatus('');
                  setBulkPreConditions('');
                  setBulkMilestone('');
                  setBulkPlatforms([]);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleBulkEditConfirm}
              >
                Apply Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={confirmDelete !== null}
        title="Delete Items"
        message={`Are you sure you want to delete ${confirmDelete?.name}? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteAction}
        onCancel={() => setConfirmDelete(null)}
        danger={true}
      />
    </div>
  );
};

export default Library;
